/**
 * Een sprong op de klantpagina: naar een onderdeel in het rapport, een regel
 * van de NHG-bijlage, of terug naar een regel van het overzicht.
 *
 * De sprong landt meteen, zonder te schuiven (de enige animatie in het product
 * is de sync-indicator), met de focus op het doel, zodat een schermlezer daar
 * verder leest. Het doel licht even op, zodat ook wie kijkt het meteen ziet.
 */

/** Wat de lezer zelf doet; dan houdt het vasthouden van het doel op. */
const READER_INPUT = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/** Zo lang blijft een doel na een sprong op zijn plek. */
const HOLD_MS = 2500;

/** Zo lang licht een doel op; aan en uit zonder overgang. */
export const HIGHLIGHT_MS = 1800;

/** Het attribuut waarmee een doel oplicht: `data-[jumped]:bg-…` in de klassen. */
export const HIGHLIGHT_ATTRIBUTE = 'data-jumped';

export interface JumpOptions {
    /** Een hoofdstuk of kop: de focus gaat naar de kop in het doel (`[data-chapter-heading]`). */
    heading?: boolean;
    /** Het doel in het midden van het scherm in plaats van bovenaan, zoals een regel in een lijst. */
    center?: boolean;
    /** Het doel een moment vasthouden terwijl er iets boven nog groeit. */
    hold?: boolean;
}

/**
 * Naar het element met dit `id`, met de focus erop. Het oplichten gebeurt op
 * de rij in het doel die `data-jump-row` draagt, anders op het doel zelf.
 *
 * Wat erboven staat kan na de sprong nog groeien - de letters komen binnen, een
 * lange tekst loopt anders af - en dan zou de lezer ergens anders uitkomen. Met
 * `hold` zet dit het doel daarom even terug op zijn plek, tot de lezer zelf
 * scrolt of iets aanraakt. Geeft terug wat het vasthouden en het oplichten
 * eerder stopt: voor een nieuwe sprong, of als het doel verdwijnt.
 */
export function jumpTo(id: string, options: JumpOptions = {}): () => void {
    const element = document.getElementById(id);

    if (!element) {
        return () => undefined;
    }

    const block = options.center ? 'center' : 'start';
    const target = options.heading
        ? (element.querySelector<HTMLElement>('[data-chapter-heading]') ?? element)
        : element;
    const row = target.closest<HTMLElement>('[data-jump-row]') ?? element;
    /* Een regel die zelf geen focus kan krijgen, geeft hem aan zijn link. */
    const focus = target.matches('a[href], button, [tabindex]')
        ? target
        : (target.querySelector<HTMLElement>('a[href], button') ?? target);

    element.scrollIntoView?.({ block });
    focus.focus({ preventScroll: true });

    row.setAttribute(HIGHLIGHT_ATTRIBUTE, '');

    const unlight = window.setTimeout(() => row.removeAttribute(HIGHLIGHT_ATTRIBUTE), HIGHLIGHT_MS);
    const release = options.hold ? hold(element, block) : () => undefined;

    return () => {
        release();
        window.clearTimeout(unlight);
        row.removeAttribute(HIGHLIGHT_ATTRIBUTE);
    };
}

function hold(element: HTMLElement, block: ScrollLogicalPosition): () => void {
    if (typeof requestAnimationFrame === 'undefined') {
        return () => undefined;
    }

    const started = performance.now();
    let expected = element.getBoundingClientRect().top;
    let frame = 0;

    const stop = () => {
        cancelAnimationFrame(frame);

        for (const type of READER_INPUT) {
            window.removeEventListener(type, stop, true);
        }
    };

    const keep = () => {
        if (!element.isConnected || performance.now() - started > HOLD_MS) {
            stop();

            return;
        }

        if (Math.abs(element.getBoundingClientRect().top - expected) > 2) {
            element.scrollIntoView?.({ block });
            expected = element.getBoundingClientRect().top;
        }

        frame = requestAnimationFrame(keep);
    };

    for (const type of READER_INPUT) {
        window.addEventListener(type, stop, { capture: true, passive: true });
    }

    frame = requestAnimationFrame(keep);

    return stop;
}

/** Een klik met een toets erbij, of met de middelste knop: die laat de pagina aan de browser (nieuw tabblad). */
export function isModifiedClick(event: {
    button: number;
    metaKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
}): boolean {
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}
